/**
 * Fetch Chatwoot SSO URL from Control Plane API
 *
 * GOTCHA G1: SSO tokens expire in 5 minutes.
 * GOTCHA: Control Plane JWT auth for allow_guest endpoints requires:
 *   1. NO Authorization header (Frappe rejects unknown Bearer tokens)
 *   2. access_token passed as body parameter
 */

import { getCPToken } from "@/lib/api/controlPlaneClient";
import type { ChatwootSsoResponse } from "./types";

const CONTROL_PLANE_URL =
  import.meta.env.VITE_CONTROL_PLANE_URL || "https://control.zaviago.com";

export async function fetchChatwootSso(
  tenantName: string,
  userEmail: string
): Promise<ChatwootSsoResponse> {
  // GOTCHA G6: Verify token exists
  const token = getCPToken();
  if (!token) {
    throw new Error("Not authenticated to Control Plane");
  }

  // CRITICAL: Do NOT use cpApiCall here - it adds Authorization header
  // which Frappe tries to validate before reaching our allow_guest endpoint.
  // Instead, pass access_token as body parameter only.
  const response = await fetch(
    `${CONTROL_PLANE_URL}/api/method/control_plane.api.chatwoot.get_sso_url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "omit",
      body: JSON.stringify({
        access_token: token,
        tenant_name: tenantName,
        email: userEmail,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    let message = "Failed to get Chatwoot SSO URL";
    if (error._server_messages) {
      try {
        const msgs = JSON.parse(error._server_messages);
        message = typeof msgs[0] === "string" ? msgs[0] : JSON.stringify(msgs[0]);
      } catch {
        message = String(error._server_messages);
      }
    } else if (error.message) {
      message = error.message;
    } else if (error.exc_type) {
      message = `${error.exc_type}: ${error.exc || "Unknown error"}`;
    }
    throw new Error(message);
  }

  const data = await response.json();
  return data.message as ChatwootSsoResponse;
}
